import { useEffect, useRef, useState } from "react";
import { Loader2, Save, Upload, Wand2, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { API, formatError } from "@/lib/api";

const L = ["A", "B", "C", "D"];
const box = "w-full rounded-xl border-2 border-slate-300 px-3 py-2 text-sm focus:border-blue-500 outline-none";

function LangBlock({ title, q, setQ, opts, setOpts, idx, setIdx, expl, setExpl, prefix }) {
  return (
    <div className="space-y-2">
      <p className="font-bold text-sm">{title}</p>
      <textarea value={q} onChange={(e) => setQ(e.target.value)} rows={2} className={box} data-testid={`${prefix}-question`} />
      {opts.map((o, i) => (
        <div key={i} className="flex items-center gap-2">
          <button type="button" onClick={() => setIdx(i)} data-testid={`${prefix}-correct-${i}`} title="Mark as the correct answer"
            className={`w-8 h-8 shrink-0 rounded-full border-2 font-bold text-sm ${idx === i ? "bg-green-500 border-green-600 text-white" : "border-slate-300 text-slate-500"}`}>
            {idx === i ? <CheckCircle2 size={16} className="mx-auto" /> : L[i]}
          </button>
          <input value={o} onChange={(e) => setOpts(opts.map((x, j) => (j === i ? e.target.value : x)))} className={box} data-testid={`${prefix}-option-${i}`} />
        </div>
      ))}
      <textarea value={expl} onChange={(e) => setExpl(e.target.value)} rows={2} placeholder="Explanation shown after answering" className={box} data-testid={`${prefix}-explanation`} />
    </div>
  );
}

export default function QuestionEditor({ target, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState("");
  const [ver, setVer] = useState(Date.now());
  const fileRef = useRef(null);

  useEffect(() => {
    if (!target) return;
    setD(null);
    api.get("/admin/question", { params: target }).then(({ data }) => {
      setD(data);
      setF({ q: data.q, options: data.options, answer_index: data.answer_index, explanation: data.explanation || "",
        q_fr: data.q_fr || "", options_fr: data.options_fr || data.options.map(() => ""), answer_index_fr: data.answer_index_fr ?? null,
        explanation_fr: data.explanation_fr || "", illustration: data.illustration || "" });
    }).catch((e) => { toast.error(formatError(e?.response?.data?.detail)); onClose(); });
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const hasFr = !!d?.q_fr;
  const run = async (kind, fn) => {
    setBusy(kind);
    try { await fn(); } catch (e) { toast.error(formatError(e?.response?.data?.detail)); } finally { setBusy(""); }
  };
  const refresh = (data) => { setD(data); setVer(Date.now()); onSaved?.(); };

  const save = () => run("save", async () => {
    const body = { ...target, ...f, q_fr: hasFr ? f.q_fr : null, options_fr: hasFr ? f.options_fr : null,
      answer_index_fr: hasFr ? f.answer_index_fr : null, explanation_fr: hasFr ? f.explanation_fr : null };
    refresh((await api.put("/admin/question", body)).data);
    toast.success("Question saved. Children see the change straight away.");
  });
  const upload = (file) => run("upload", async () => {
    const fd = new FormData();
    Object.entries(target).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    const { data } = await api.post("/admin/question/picture", fd);
    refresh(data); set("illustration")(data.illustration || "");
    toast.success("Picture uploaded.");
  });
  const redraw = () => run("redraw", async () => {
    await api.post("/admin/question/redraw", { ...target, description: f.illustration });
    toast.message("Drawing the new picture… (about 1 minute)");
    for (let i = 0; i < 40; i++) {
      await new Promise((r) => setTimeout(r, 4000));
      const { data } = await api.get("/admin/question/redraw-status", { params: target });
      if (data.status !== "drawing") {
        refresh((await api.get("/admin/question", { params: target })).data);
        data.status === "done" ? toast.success("New picture ready.") : toast.error("The AI could not draw a matching picture. Try another description or upload one.");
        return;
      }
    }
  });
  const remove = () => run("remove", async () => {
    refresh((await api.post("/admin/question/remove-picture", target)).data);
    set("illustration")("");
    toast.success("Picture removed. Remember to remove “Look at the picture” from the question.");
  });

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="question-editor">
        <DialogHeader>
          <DialogTitle>Edit question {d ? `· ${d.subject} G${d.grade} · level ${d.level} · Q${d.qi + 1}` : ""}</DialogTitle>
        </DialogHeader>
        {!f || !d ? <Loader2 className="animate-spin mx-auto" /> : (
          <div className="space-y-5">
            <div className="rounded-2xl border-2 border-slate-200 p-3 flex flex-col sm:flex-row gap-3" data-testid="editor-picture">
              <div className="w-48 h-36 shrink-0 rounded-xl bg-slate-50 border flex items-center justify-center overflow-hidden">
                {d.has_picture ? <img src={`${API}/question-image?chapter_id=${encodeURIComponent(d.chapter_id)}&level=${d.level}&qi=${d.qi}&lang=en&v=${ver}`}
                  alt="" className="max-w-full max-h-full object-contain" data-testid="editor-picture-img" /> : <span className="text-xs text-slate-400">No picture</span>}
              </div>
              <div className="flex-1 space-y-2">
                <p className="font-bold text-sm">Picture description (what the drawing must show, no words in it)</p>
                <textarea value={f.illustration} onChange={(e) => set("illustration")(e.target.value)} rows={2} className={box}
                  placeholder="e.g. a weather anemometer with three cups on a pole" data-testid="editor-illustration" />
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={redraw} disabled={!!busy || f.illustration.trim().length < 5} data-testid="editor-redraw"
                    className="rounded-full bg-blue-600 text-white px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50">
                    {busy === "redraw" ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} Redraw from description</button>
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid="editor-upload"
                    className="rounded-full border-2 border-slate-900 px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50">
                    {busy === "upload" ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload my picture</button>
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden data-testid="editor-upload-input"
                    onChange={(e) => { e.target.files[0] && upload(e.target.files[0]); e.target.value = ""; }} />
                  <button type="button" onClick={remove} disabled={!!busy || !d.has_picture} data-testid="editor-remove-picture"
                    className="rounded-full border-2 border-rose-400 text-rose-600 px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50">
                    <Trash2 size={13} /> Remove picture</button>
                </div>
              </div>
            </div>
            <div className={`grid gap-5 ${hasFr ? "md:grid-cols-2" : ""}`}>
              <LangBlock title={hasFr ? "English" : "Question"} prefix="editor-en" q={f.q} setQ={set("q")} opts={f.options} setOpts={set("options")}
                idx={f.answer_index} setIdx={set("answer_index")} expl={f.explanation} setExpl={set("explanation")} />
              {hasFr && <LangBlock title="Français" prefix="editor-fr" q={f.q_fr} setQ={set("q_fr")} opts={f.options_fr} setOpts={set("options_fr")}
                idx={f.answer_index_fr} setIdx={set("answer_index_fr")} expl={f.explanation_fr} setExpl={set("explanation_fr")} />}
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="rounded-full px-4 py-2 text-sm font-semibold" data-testid="editor-cancel">Close</button>
              <button type="button" onClick={save} disabled={!!busy} data-testid="editor-save"
                className="rounded-full bg-slate-900 text-white px-5 py-2 text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
                {busy === "save" ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save question</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
